import { ACT_NAMES, ENCOUNTERS } from "./content";
import { pick, stream, weightedPick } from "./rng";
import type { ActMap, MapNode, NodeType } from "./types";

const ROW_POOLS: Record<number, { item: NodeType; weight: number }[]> = {
  0: [{ item: "battle", weight: 1 }],
  1: [{ item: "battle", weight: 5 }, { item: "event", weight: 3 }, { item: "shop", weight: 1 }],
  2: [{ item: "battle", weight: 4 }, { item: "elite", weight: 2 }, { item: "event", weight: 2 }, { item: "shop", weight: 1 }],
  3: [{ item: "battle", weight: 3 }, { item: "rest", weight: 3 }, { item: "event", weight: 2 }],
  4: [{ item: "battle", weight: 4 }, { item: "elite", weight: 3 }, { item: "event", weight: 2 }, { item: "treasure", weight: 1 }],
  5: [{ item: "battle", weight: 3 }, { item: "shop", weight: 2 }, { item: "rest", weight: 2 }, { item: "elite", weight: 1 }],
  6: [{ item: "rest", weight: 5 }, { item: "battle", weight: 2 }, { item: "event", weight: 1 }],
};

export function generateMap(seed: string, act: number): ActMap {
  const rng = stream(seed, `map-act-${act}`);
  const nodes: MapNode[] = [];

  for (let row = 0; row < 7; row += 1) {
    for (let column = 0; column < 3; column += 1) {
      let type = weightedPick(ROW_POOLS[row], rng);
      if (row === 0) type = "battle";
      if (row === 6 && column === 1) type = "rest";
      nodes.push({
        id: `a${act}-r${row}-c${column}`,
        row,
        column,
        type,
        connections: [],
        visited: false,
        accessible: row === 0,
      });
    }
  }

  const boss: MapNode = {
    id: `a${act}-boss`,
    row: 7,
    column: 1,
    type: "boss",
    connections: [],
    visited: false,
    accessible: false,
  };
  nodes.push(boss);

  for (let row = 0; row < 6; row += 1) {
    for (let column = 0; column < 3; column += 1) {
      const current = nodes.find((node) => node.row === row && node.column === column)!;
      const candidates = nodes.filter((node) => node.row === row + 1 && Math.abs(node.column - column) <= 1);
      const shuffled = [...candidates].sort(() => rng() - 0.5);
      const desired = rng() < 0.55 ? 2 : 1;
      current.connections = shuffled.slice(0, desired).map((node) => node.id);
    }
  }

  // Ensure every node after the first row has at least one incoming route.
  for (let row = 1; row < 7; row += 1) {
    for (const node of nodes.filter((entry) => entry.row === row)) {
      const incoming = nodes.filter((entry) => entry.row === row - 1 && entry.connections.includes(node.id));
      if (!incoming.length) {
        const previous = nodes.filter((entry) => entry.row === row - 1).sort((a, b) => Math.abs(a.column - node.column) - Math.abs(b.column - node.column));
        previous[0].connections.push(node.id);
      }
    }
  }

  for (const node of nodes.filter((entry) => entry.row === 6)) node.connections = [boss.id];

  // Avoid all-three duplicate special rows and guarantee route texture.
  for (let row = 1; row < 7; row += 1) {
    const rowNodes = nodes.filter((entry) => entry.row === row);
    if (rowNodes.every((entry) => entry.type === rowNodes[0].type) && rowNodes[0].type !== "battle") {
      rowNodes[pick([0, 2], rng)].type = "battle";
    }
  }

  return {
    act,
    name: ACT_NAMES[act],
    nodes,
    bossId: ENCOUNTERS[act].boss,
  };
}
